import { CloseOutlined, ExpandOutlined, FormOutlined } from '@ant-design/icons';
import { useCallback, useEffect, useRef, useState } from 'react';
import { LuFileText, LuGitCompare, LuPenLine } from 'react-icons/lu';

import { useCopilot } from '../CopilotContext';
import { ChatPanel } from '../chat/ChatPanel/ChatPanel';
import { Composer } from '../chat/Composer/Composer';
import { ListeningIndicator } from '../chat/ListeningIndicator/ListeningIndicator';
import { Suggestions } from '../chat/Suggestions/Suggestions';
import { TypingIndicator } from '../chat/TypingIndicator/TypingIndicator';
import { ScanProgressStrip } from '../ScanProgressStrip/ScanProgressStrip';
import {
  openCopilotPopout,
  usePopoutVisibility,
} from '../popout/popoutChannel';
import { ChatMenu } from './ChatMenu/ChatMenu';
import { useNewChat } from './Sidebar/useNewChat';

import styles from './CopilotWidget.module.css';

const SUGGESTIONS = [
  { id: 'summarize', label: 'Summarize this study', icon: LuFileText },
  { id: 'compare', label: 'Compare with the prior', icon: LuGitCompare },
  { id: 'draft', label: 'Draft the conclusion', icon: LuPenLine },
] as const;

const SUGGESTION_PROMPTS: Record<string, string> = {
  summarize:
    'Give me a quick summary of this study — the key measurements and anything out of range.',
  compare:
    'How does this study compare to the most recent prior for this patient?',
  draft:
    'Help me draft the conclusion for this study from the measurements and AI findings.',
};

const WIDTH_KEY = 'copilot.panel.width';
const MIN_WIDTH = 320;
const MAX_WIDTH = 560;
const DEFAULT_WIDTH = 360;

function clampWidth(w: number): number {
  return Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, w));
}

function readStoredWidth(): number {
  try {
    const n = parseInt(localStorage.getItem(WIDTH_KEY) ?? '', 10);
    return Number.isFinite(n) ? clampWidth(n) : DEFAULT_WIDTH;
  } catch {
    return DEFAULT_WIDTH;
  }
}

export function CopilotWidget() {
  const {
    enabled,
    isOpen,
    close,
    messages,
    sendText,
    isLoadingMessages,
    activeStudyUuid,
  } = useCopilot();
  const popoutOpen = usePopoutVisibility();
  const newChat = useNewChat();

  const [render, setRender] = useState(isOpen);
  const [expanded, setExpanded] = useState(isOpen);
  const [width, setWidth] = useState(readStoredWidth);
  const [resizing, setResizing] = useState(false);
  // Teardown for an in-flight drag, so we can also run it on unmount.
  const endResizeRef = useRef<(() => void) | null>(null);

  // Mount on open, then expand on the *second* frame so the width:0 start
  // value is painted first (a single rAF can be coalesced into the same paint
  // and skip the transition); on close, collapse then unmount after 220ms.
  useEffect(() => {
    if (isOpen) {
      setRender(true);
      let inner = 0;
      const outer = window.requestAnimationFrame(() => {
        inner = window.requestAnimationFrame(() => setExpanded(true));
      });
      return () => {
        window.cancelAnimationFrame(outer);
        window.cancelAnimationFrame(inner);
      };
    }
    if (!render) return undefined;
    setExpanded(false);
    const t = window.setTimeout(() => setRender(false), 220);
    return () => window.clearTimeout(t);
  }, [isOpen, render]);

  const startResize = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      const startX = e.clientX;
      const startWidth = width;
      let latest = startWidth;
      setResizing(true);
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'col-resize';

      function onMove(ev: PointerEvent) {
        // Handle is on the panel's left edge: dragging left widens it.
        latest = clampWidth(startWidth + (startX - ev.clientX));
        setWidth(latest);
      }
      // One teardown for pointerup, pointercancel, and unmount — restores body
      // styles, drops the listeners, and persists the final width.
      function onEnd() {
        document.body.style.userSelect = '';
        document.body.style.cursor = '';
        document.removeEventListener('pointermove', onMove);
        document.removeEventListener('pointerup', onEnd);
        document.removeEventListener('pointercancel', onEnd);
        endResizeRef.current = null;
        setResizing(false);
        try {
          localStorage.setItem(WIDTH_KEY, String(latest));
        } catch {
          /* ignore */
        }
      }

      endResizeRef.current = onEnd;
      document.addEventListener('pointermove', onMove);
      document.addEventListener('pointerup', onEnd);
      document.addEventListener('pointercancel', onEnd);
    },
    [width]
  );

  // Tear down a drag in progress if the panel unmounts mid-resize.
  useEffect(() => () => endResizeRef.current?.(), []);

  if (!enabled) return null;
  if (popoutOpen) return null;
  if (!render) return null;

  const hasMessages = messages.length > 0;
  const showChatPanel = hasMessages || isLoadingMessages;

  return (
    <aside
      className={`${styles.panel} ${resizing ? styles.resizing : ''}`}
      style={{ width: expanded ? width : 0 }}
      aria-label="ECHOGPT"
    >
      <div
        className={styles.resizeHandle}
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize Copilot panel"
        onPointerDown={startResize}
      />

      <div className={styles.inner} style={{ width }}>
        <header className={styles.header}>
          <ChatMenu />
          <div className={styles.headerActions}>
            <button
              type="button"
              className={styles.iconBtn}
              aria-label="New chat"
              onClick={newChat}
            >
              <FormOutlined />
            </button>
            <button
              type="button"
              className={styles.iconBtn}
              aria-label="Open in new window"
              onClick={() => openCopilotPopout(activeStudyUuid)}
            >
              <ExpandOutlined />
            </button>
            <button
              type="button"
              className={styles.iconBtn}
              aria-label="Close"
              onClick={close}
            >
              <CloseOutlined />
            </button>
          </div>
        </header>

        <div className={styles.chatColumn}>
          {!showChatPanel ? (
            <div className={styles.empty}>
              <div className={styles.greeting}>Hi, how can I help?</div>
              <Suggestions
                items={SUGGESTIONS}
                onPick={(id) => sendText(SUGGESTION_PROMPTS[id])}
              />
            </div>
          ) : (
            <ChatPanel />
          )}

          {!showChatPanel && (
            <p className={styles.disclaimer}>
              Responses are AI-generated and for clinical reference only. Always
              verify before use.
            </p>
          )}

          <ScanProgressStrip />

          <TypingIndicator />

          <ListeningIndicator />

          <div className={styles.composer}>
            <Composer />
          </div>
        </div>
      </div>
    </aside>
  );
}
